import * as ko from "knockout";
import {_} from 'lodash';
import {ObjectTypeId, Permission, REQUEST_THROTTLE, SNACKBAR_DURATION_LONG, SYSTEM_MEGA_EADM,} from "../constants";
import {
    IdName,
    IImportFormat,
    IMessageData,
    IObjectData,
    IObjectType,
    IOrganization,
    ISyncField,
    ISystem,
    ISystemRole,
    TBaseOrObservableOrFunctionType,
    TBaseOrObservableType,
    TDataSourceType,
    TRouteId,
    TSyncfield
} from "../index";
import {config, global, lm, lm_} from "../appContext";
import {getDateTime, validateServerDate} from "./datetime";
import {nsFa} from "../component/fa/fn";
import {storeObservable} from "./sessionstore";
import {TServiceName} from "../constants/ApiServices";
import {ERROR_MESSAGE} from "../constants/Language";
import {nsSyncFields} from "../req/nsSyncFields";
import {getHash, getRouteDefinition} from "../constants/RouteDefinitions";
import {nsLanguage} from "./nsLanguage";
import {TSystemPartial, TSystemRolePartial} from "../component/combobox/system-role";
import {ComponentRegistry} from "../constants/ComponentRegistry";
import {nsDataSource} from "./nsDataSource";
import {nsDom} from "./dom";
import {isNullOrUndefined, isNullOrUndefinedOrEmptyString, getAnyValue} from "./value";
import {_d, wireDebugConsole} from "./debugConsole";
export {_d, wireDebugConsole};
export {isNullOrUndefined, isNullOrUndefinedOrEmptyString, getAnyValue} from "./value";
import i = nsFa.i;
import toLocaleString = nsLanguage.toLocaleString;

export function logDecorator(target: any, key: any, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;

    descriptor.value = function (...args: any[]) {
        let functionName = key;
        console.log(functionName + "(" + args.join(", ") + ")", '=>');
        let result = originalMethod.apply(this, args);
        console.log(result);
        return result;
    };

    return descriptor;
}

export function pad(n: number, width: number, z: string = '0'): string {
    let str = n + '';
    return str.length >= width ? str : new Array(width - str.length + 1).join(z) + str;
}

(<any>ko.subscribable.fn).subscribeChanged = function (callback: (newValue: any, oldValue: any) => void) {
    let oldValue: any;
    this.subscribe(function (_oldValue: any) {
        oldValue = _oldValue;
    }, this, 'beforeChange');

    return this.subscribe(function (newValue: any) {
        callback(newValue, oldValue);
    });
};

export function generateHash(str: string): string {
    let hash = 0, i, chr;
    if (str.length === 0) return String(hash);
    for (i = 0; i < str.length; i++) {
        chr = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return String(hash);
}

export function uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

export interface IPermissionDefinition {
    id: number
    displayName: string
    serverName: string | null
}

export const permissionDefinitions: Record<Permission, IPermissionDefinition> = {
    [Permission.LoggedIn]: {
        id: Permission.LoggedIn,
        displayName: '',
        serverName: null,
    },
    [Permission.None]: {
        id: Permission.None,
        displayName: 'none',
        serverName: 'None',
    },
    [Permission.Employee]: {
        id: Permission.Employee,
        displayName: 'Employee',
        serverName: 'Employee',
    },
    [Permission.SuperEmployee]: {
        id: Permission.SuperEmployee,
        displayName: 'SuperEmployee',
        serverName: 'SuperEmployee',
    },
    [Permission.DepartmentAdmin]: {
        id: Permission.DepartmentAdmin,
        displayName: 'department manager|c',
        serverName: 'Manager',
    },
    [Permission.SuperManager]: {
        id: Permission.SuperManager,
        displayName: 'SuperManager',
        serverName: 'SuperManager',
    },
    [Permission.CompanyAdmin]: {
        id: Permission.CompanyAdmin,
        displayName: 'CompanyAdmin',
        serverName: 'CompanyAdmin',
    },
    [Permission.OrganizationAdmin]: {
        id: Permission.OrganizationAdmin,
        displayName: 'organizationAdmin',
        serverName: 'OrganizationAdmin',
    },
    [Permission.SuperAdmin]: {
        id: Permission.SuperAdmin,
        displayName: 'SuperAdmin',
        serverName: 'SuperAdmin',
    }
}

export function getPermissionDefinition(permission: Permission): IPermissionDefinition {
    return permissionDefinitions[permission];
}

export function getPermissionDefinitionArray(): IPermissionDefinition[] {
    return [
        Permission.LoggedIn,
        Permission.None,
        Permission.Employee,
        Permission.SuperEmployee,
        Permission.DepartmentAdmin,
        Permission.SuperManager,
        Permission.CompanyAdmin,
        Permission.OrganizationAdmin,
        Permission.SuperAdmin,
    ].map(getPermissionDefinition)
}

export function getPermissionDisplayName(role: ISystemRole, system?: ISystem): string | null {
    if (!role) {
        return null
    }
    if (system?.name != SYSTEM_MEGA_EADM) {
        return role.name
    }
    return lm.t(getPermissionDefinition(role.roleenum as Permission)?.displayName ?? role.name, {c: true})
}

export function validateEmail(email: string): boolean {
    let regExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regExp.test(email);
}

export function validateOrgNr(orgnr: string): boolean {
    let regExp = /^NO\d{9}$/;
    return regExp.test(orgnr);
}

export function validateObjectGuid(str: string) {
    const regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return regex.test(str);
}


export function findOrganizationById(id: string, organizations: IOrganization[]): IOrganization | undefined {
    if (!organizations) {
        return undefined;
    }
    return organizations.find((org: IOrganization) => {
        return org.organizationid == id;
    });
}

export function downloadCSV(csv: string, filename: string = "export.csv") {
    if (csv == null) return;

    let link = document.createElement('a');
    link.setAttribute('href', "data:text/csv;charset=utf-8,%EF%BB%BF" + encodeURIComponent(csv));
    link.setAttribute('download', filename);
    link.click();
}

// FIXME: this shouldn't be necessary.
export function normalizeObjectType(objectType: IObjectType) {
    if (!objectType)
        return;
    switch (objectType.name) {
        case "User":
            objectType.id = ObjectTypeId.User;
            break;
        case "Department":
            objectType.id = ObjectTypeId.Department;
            break;
        case "Group":
            objectType.id = ObjectTypeId.Group;
            break;
    }
}

export function getWebServiceUrl(serviceName: TServiceName): string {
    return config.getUrlPrefix() + config.getUrlFile() + serviceName
}

export function getSyncField<T extends { name: string } = ISyncField>(key: TSyncfield, arr: T[]): T | undefined {
    return arr?.find((item) => item.name == key);
}

export function getRawSyncFieldValue(key: TSyncfield | TSyncfield[], arr: nsSyncFields.TSyncfield_basic[]): string | undefined {
    if (Array.isArray(key)) {
        const k = key.find(k => getRawSyncFieldValue(k, arr) !== undefined);
        return k === undefined ? undefined : getRawSyncFieldValue(k, arr);
    }
    return getSyncField(key, arr)?.value;
}

export function getSyncFieldValue(key: TSyncfield | TSyncfield[], arr: ISyncField[]): string {
    const val = getRawSyncFieldValue(key, arr);
    if (val === undefined) {
        return "—";
    }
    if (validateServerDate(val)) {
        return getDateTime(val, true);
    }
    return val;
}

export function getSyncFieldDisplayName(objectTypeId: ObjectTypeId, syncfields: ISyncField[]): string {
    switch (objectTypeId) {
        case ObjectTypeId.User:
        case ObjectTypeId.ApiUser:
        case ObjectTypeId.ExternalContact: {
            const rawSyncFieldValue = getRawSyncFieldValue('DisplayName', syncfields)
            if (rawSyncFieldValue) {
                return rawSyncFieldValue
            }
            return (['GivenName', 'Surname'] as const)
                .map((k) => getRawSyncFieldValue(k, syncfields))
                .filter(Boolean)
                .join(' ')
                ;
        }
        default: {
            const objDef = getObjectDefinition(objectTypeId);
            const keys = objDef?.syncfieldDisplayName;
            if (!keys) {
                return lm.t('unknown') ?? 'unknown';
            }
            return keys.map((k: TSyncfield) => getRawSyncFieldValue(k, syncfields)).find(str => !!str) ?? lm.t('unknown') ?? 'unknown';
        }
    }
}

export function createSnackbarErrorMessage(message: string, reason: string) {
    global.snackbarData(_.extend(
        createErrorMessage(message, reason),
        {
            message: message,
            type: "danger",
            duration: SNACKBAR_DURATION_LONG
        }
    ));
}

export function createErrorMessage(reason: string, message: string = ERROR_MESSAGE): IMessageData {
    _d.error(reason)
    message = `((${message}))`

    if (reason) {
        const reasonWhiteWashed = String(reason).replace(/\(/gm, '[').replace(/\)/gm, ']')
        const routeDef = getRouteDefinition('logerrorsearch');
        const hasPermission = routeDef
            && getEadmRole(global.login.selectedOrganization() ?? undefined) >= (routeDef.permissionLevel ?? Permission.None)
        const isGuid = validateObjectGuid(reasonWhiteWashed)
        message += `<br><span class="error-message-reason">((reason)): `
        if (hasPermission && isGuid) {
            message += `<a href="${getHash('logerrorsearch', {searchkey: reasonWhiteWashed})}">((${reasonWhiteWashed}))</a></span>`
        } else {
            message += `((${reasonWhiteWashed}))`
        }
        message += `</span>`
    }

    return {
        message: message,
        type: "danger"
    };
}

export function createErrorString(reason: string, target: string | null = null): string {
    const reasonWhiteWashed = String(reason).replace(/\(/gm, '[').replace(/\)/gm, ']')
    const routeDef = getRouteDefinition('logerrorsearch');
    const hasPermission = routeDef
        && getEadmRole(global.login.selectedOrganization() ?? undefined) >= (routeDef.permissionLevel ?? Permission.None)
    const isGuid = validateObjectGuid(reasonWhiteWashed)
    if (hasPermission && isGuid) {
        return `<a href="${getHash('logerrorsearch', {searchkey: reasonWhiteWashed})}" target="${target ?? ''}">((${reasonWhiteWashed}))</a></span>`
    } else {
        return `((${reasonWhiteWashed}))`
    }
}

export function getTelLink(phoneNumber: string, icon: string = 'fa-phone') {
    return `${i(icon)} <a href="tel:${phoneNumber}">${phoneNumber}</a>`
}

export function getEmailLink(email: string, icon: string = 'fa-envelope') {
    return `${i(icon)} <a href="mailto:${email}">${email}</a>`
}

export function setCaretPosition(elem: any, caretPos: number) {
    if (elem != null) {
        if (elem.createTextRange) {
            const range = elem.createTextRange();
            range.move('character', caretPos);
            range.select();
        } else {
            if (elem.selectionStart) {
                elem.focus();
                elem.setSelectionRange(caretPos, caretPos);
            } else
                elem.focus();
        }
    }
}

export interface IObjectDefinition {
    displayNameExpression: string | null
    icon: string
    iconManual: string | null
    id: ObjectTypeId
    name: string
    plural: string
    pluralLowerCase: string
    routeDetails?: TRouteId
    routeDetailsManual?: TRouteId
    routeSearch?: TRouteId
    routeSearchManual?: TRouteId
    singular: string
    singularLowerCase: string
    syncfieldDisplayName: TSyncfield[] | null
    translation: string
    translationManual?: string
}


const ObjectDefinitions: Record<ObjectTypeId, IObjectDefinition> = {
    [ObjectTypeId.User]: {
        displayNameExpression: '[IFEMPTYUSE;[DisplayName];[GivenName] [Surname]]',
        icon: 'fa-user',
        iconManual: 'fa-manual-user',
        id: ObjectTypeId.User,
        name: 'User',
        routeDetails: 'userdetails',
        routeDetailsManual: 'manualuserdetails',
        routeSearch: 'usersearch',
        routeSearchManual: 'manualusersearch',
        /**
         translation: 'user',
         * Note that syncfieldDisplayName is not really used in getObjectDisplayNameFn
         * @see getObjectDisplayNameFn
         */
        syncfieldDisplayName: ['DisplayName', 'GivenName', 'Surname'],
        translation: 'user',
        translationManual: 'manual user',
        plural: 'user|p',
        pluralLowerCase: 'user|p|ac',
        singular: 'user',
        singularLowerCase: 'user|ac',
    },
    [ObjectTypeId.Group]: {
        displayNameExpression: '[IFEMPTYUSE;[IFEMPTYUSE;[Name];[SourceId]];[Description]]',
        icon: 'fa-users',
        iconManual: 'fa-manual-group',
        id: ObjectTypeId.Group,
        name: 'Group',
        routeDetails: 'groupdetails',
        routeDetailsManual: 'manualgroupdetails',
        routeSearch: 'groupsearch',
        routeSearchManual: 'manualgroupsearch',
        syncfieldDisplayName: ['Name', 'Description'],
        translation: 'group',
        translationManual: 'manual group',
        plural: 'group|p',
        pluralLowerCase: 'group|p|ac',
        singular: 'group',
        singularLowerCase: 'group|ac',
    },
    [ObjectTypeId.Department]: {
        displayNameExpression: '[IFEMPTYUSE;[IFEMPTYUSE;[Name];[Department]];[SourceId]]',
        icon: 'fa-sitemap',
        iconManual: 'fa-manual-department',
        id: ObjectTypeId.Department,
        name: 'Department',
        routeDetails: 'departmentdetails',
        routeDetailsManual: 'manualdepartmentdetails',
        routeSearch: 'departmentsearch',
        routeSearchManual: 'manualdepartmentsearch',
        syncfieldDisplayName: ['Department', 'Name', 'SourceId', 'DepartmentNumber', 'OrgUnitnr'],
        translation: 'department',
        translationManual: 'manual department',
        plural: 'department|p',
        pluralLowerCase: 'department|p|ac',
        singular: 'department',
        singularLowerCase: 'department|ac',
    },
    [ObjectTypeId.LegalUnit]: {
        displayNameExpression: '[IFEMPTYUSE;[Name];[SourceId]]',
        icon: 'fa-building',
        iconManual: null,
        id: ObjectTypeId.LegalUnit,
        name: 'LegalUnit',
        routeDetails: 'legalunitdetails',
        routeSearch: 'legalunitsearch',
        syncfieldDisplayName: ['Name', 'SourceId'],
        translation: 'legalunit',
        plural: 'legalunit|p',
        pluralLowerCase: 'legalunit|p|ac',
        singular: 'legalunit',
        singularLowerCase: 'legalunit|ac',
    },
    [ObjectTypeId.Function]: {
        displayNameExpression: '[IFEMPTYUSE;[Name];[DisplayName]]',
        icon: 'fa-function',
        iconManual: null,
        id: ObjectTypeId.Function,
        name: 'Function',
        syncfieldDisplayName: ['Name', 'DisplayName'],
        translation: 'function',
        plural: 'function|p',
        pluralLowerCase: 'function|p|ac',
        singular: 'function',
        singularLowerCase: 'function|ac',
    },
    [ObjectTypeId.History]: {
        displayNameExpression: '[IFEMPTYUSE;[Name];[DisplayName]]',
        icon: 'fa-history',
        iconManual: null,
        id: ObjectTypeId.History,
        name: 'History',
        syncfieldDisplayName: ['Name', 'DisplayName'],
        translation: 'history',
        plural: 'history|p',
        pluralLowerCase: 'history|p|ac',
        singular: 'history',
        singularLowerCase: 'history|ac',
    },
    [ObjectTypeId.Event]: {
        displayNameExpression: '[IFEMPTYUSE;[Name];[DisplayName]]',
        icon: 'fa-calendar-exclamation',
        iconManual: null,
        id: ObjectTypeId.Event,
        name: 'Event',
        syncfieldDisplayName: ['Name', 'DisplayName'],
        translation: 'event',
        plural: 'event|p',
        pluralLowerCase: 'event|p|ac',
        singular: 'event',
        singularLowerCase: 'event|ac',
    },
    [ObjectTypeId.System]: {
        displayNameExpression: null,
        icon: 'fa-cogs',
        iconManual: null,
        id: ObjectTypeId.System,
        name: 'System',
        syncfieldDisplayName: null,
        translation: 'system',
        plural: 'system|p',
        pluralLowerCase: 'system|p|ac',
        singular: 'system',
        singularLowerCase: 'system|ac',
    },
    [ObjectTypeId.FormData]: {
        displayNameExpression: '[FormName]',
        icon: 'fa-brackets',
        iconManual: null,
        id: ObjectTypeId.FormData,
        name: 'FormData',
        routeDetails: 'formdatadetails',
        syncfieldDisplayName: ['FormName'],
        translation: 'formdata',
        translationManual: 'manual formdata',
        plural: 'formdata|p',
        pluralLowerCase: 'formdata|p|ac',
        singular: 'formdata',
        singularLowerCase: 'formdata|ac',
    },
    [ObjectTypeId.Message]: {
        displayNameExpression: null,
        icon: 'fa-envelope',
        iconManual: null,
        id: ObjectTypeId.Message,
        name: 'Message',
        syncfieldDisplayName: null,
        translation: 'messageflow',
        plural: 'messageflow|p',
        pluralLowerCase: 'messageflow|p|ac',
        singular: 'messageflow',
        singularLowerCase: 'messageflow|ac',
    },
    [ObjectTypeId.Ruleset]: {
        displayNameExpression: null,
        icon: 'fa-ruleset',
        iconManual: null,
        id: ObjectTypeId.Ruleset,
        name: 'Ruleset',
        routeDetails: 'rulesetdetails',
        routeSearch: 'rulesetsearch',
        syncfieldDisplayName: null,
        translation: 'ruleset',
        plural: 'ruleset|p',
        pluralLowerCase: 'ruleset|p|ac',
        singular: 'ruleset',
        singularLowerCase: 'ruleset|ac',
    },
    [ObjectTypeId.FunctionAlias]: {
        displayNameExpression: null,
        icon: 'fa-function-alias',
        iconManual: null,
        id: ObjectTypeId.FunctionAlias,
        name: 'FunctionAlias',
        syncfieldDisplayName: null,
        translation: 'functionalias',
        plural: 'functionalias|p',
        pluralLowerCase: 'functionalias|p|ac',
        singular: 'functionalias',
        singularLowerCase: 'functionalias|ac',
    },
    [ObjectTypeId.ExternalContact]: {
        displayNameExpression: '[IFEMPTYUSE;[DisplayName];[GivenName] [Surname]]',
        icon: 'fa-external-contact',
        iconManual: null,
        id: ObjectTypeId.ExternalContact,
        name: 'ExternalContact',
        syncfieldDisplayName: ['DisplayName', 'GivenName', 'Surname'],
        translation: 'externalcontact',
        plural: 'externalcontact|p',
        pluralLowerCase: 'externalcontact|p|ac',
        singular: 'externalcontact',
        singularLowerCase: 'externalcontact|ac',
    },
    [ObjectTypeId.FormFunction]: {
        displayNameExpression: null,
        icon: 'fa-code',
        iconManual: null,
        id: ObjectTypeId.FormFunction,
        name: 'FormFunction',
        syncfieldDisplayName: null,
        translation: 'formfunction',
        plural: 'formfunction|p',
        pluralLowerCase: 'formfunction|p|ac',
        singular: 'formfunction',
        singularLowerCase: 'formfunction|ac',
    },
    [ObjectTypeId.ApiUser]: {
        displayNameExpression: null,
        icon: 'fa-cloud-user',
        iconManual: null,
        id: ObjectTypeId.ApiUser,
        name: 'ApiUser',
        plural: 'apiuser|p',
        // |ac anticapitalizes only the first char — breaks "API-brukere" → "aPI-brukere"
        pluralLowerCase: 'apiuser|p',
        routeDetails: 'apiuserdetails',
        routeDetailsManual: 'manualapiuserdetails',
        singular: 'apiuser',
        singularLowerCase: 'apiuser|ac',
        syncfieldDisplayName: ['DisplayName', 'GivenName', 'Surname'],
        translation: 'apiuser',
    },
    [ObjectTypeId.ClientCredential]: {
        displayNameExpression: '[IFEMPTYUSE;[Name];[Description]]',
        icon: 'fa-key',
        iconManual: null,
        id: ObjectTypeId.ClientCredential,
        name: 'ClientCredential',
        syncfieldDisplayName: ['Name', 'Description'],
        translation: 'clientcredential',
        plural: 'clientcredential|p',
        pluralLowerCase: 'clientcredential|p|ac',
        singular: 'clientcredential',
        singularLowerCase: 'clientcredential|ac',
    },
    [ObjectTypeId.QueryAlias]: {
        displayNameExpression: null,
        icon: 'fa-query-alias',
        iconManual: null,
        id: ObjectTypeId.QueryAlias,
        name: 'QueryAlias',
        syncfieldDisplayName: null,
        translation: 'queryalias',
        plural: 'queryalias|p',
        pluralLowerCase: 'queryalias|p|ac',
        singular: 'queryalias',
        singularLowerCase: 'queryalias|ac',
    },
    [ObjectTypeId.FEStandard]: {
        displayNameExpression: null,
        icon: 'fa-fe-standard',
        iconManual: null,
        id: ObjectTypeId.FEStandard,
        name: 'FE Standard',
        syncfieldDisplayName: null,
        translation: 'fe standard',
        plural: 'fe standard|p',
        pluralLowerCase: 'fe standard|p|ac',
        singular: 'fe standard',
        singularLowerCase: 'fe standard|ac',
    },
    [ObjectTypeId.BEStandard]: {
        displayNameExpression: null,
        icon: 'fa-be-standard',
        iconManual: null,
        id: ObjectTypeId.BEStandard,
        name: 'BE Standard',
        syncfieldDisplayName: null,
        translation: 'be standard',
        plural: 'be standard|p',
        pluralLowerCase: 'be standard|p|ac',
        singular: 'be standard',
        singularLowerCase: 'be standard|ac',
    },
};

export function getObjectDefinition(obj: ObjectTypeId | IObjectType | null | undefined): IObjectDefinition | null {
    if (!obj) {
        return null;
    }
    if (typeof obj == 'number') {
        return ObjectDefinitions[obj] ?? null;
    }
    return ObjectDefinitions[obj.id] ?? null;
}

export function getObjectType(objectTypeId: ObjectTypeId): IObjectType {
    const def = getObjectDefinition(objectTypeId);
    return {
        id: objectTypeId,
        name: def?.name ?? String(objectTypeId),
    }
}

export function getObjectDisplayNameFn(objectTypeId: ObjectTypeId): ((obj: nsSyncFields.TObjectData_basic) => string) {
    switch (objectTypeId) {
        case ObjectTypeId.ApiUser:
        case ObjectTypeId.User:
        case ObjectTypeId.ExternalContact:
        case ObjectTypeId.Department:
        case ObjectTypeId.LegalUnit:
        case ObjectTypeId.Group:
        case ObjectTypeId.FormData:
            return (obj: nsSyncFields.TObjectData_basic) => getSyncFieldDisplayName(objectTypeId, obj.syncfields)
        default:
            _d.warn('No implementation for object type id', objectTypeId);
            return () => 'N/A';
    }
}

export function getObjectDisplayName(obj: nsSyncFields.TObjectData_basic | null | undefined): string | null {
    if (!obj) {
        return null
    }
    const objectTypeId = obj.type.id;
    return getObjectDisplayNameFn(objectTypeId)(obj);
}

export function getEadmRole(org?: IOrganization | null): Permission {
    const resolvedOrg = org ?? global.login.selectedOrganization() ?? undefined;
    const permissions = resolvedOrg?.systempermission?.filter(sp => sp.system.name == SYSTEM_MEGA_EADM);
    if (permissions?.length) {
        return Math.max(...permissions.map(p => p.role.roleenum));
    }
    return Permission.None;
}

export function isEadmRoleGreaterOrEqual(
    targetOrg: IOrganization | null | undefined,
    sourceOrg: IOrganization | null | undefined = global.login.selectedOrganization(),
): boolean {
    if (!targetOrg || !sourceOrg) {
        return false;
    }
    const targetRole = getEadmRole(targetOrg)
    const sourceRole = getEadmRole(sourceOrg);
    return targetRole <= sourceRole;
}

export function getOrganizationFromId(
    organizationId: string,
    organizations: IOrganization[] = global.login.organizations(),
): IOrganization | undefined {
    return organizations?.find(o => o.organizationid == organizationId);
}

export function canManageOthersPassword(org?: IOrganization | null): boolean {
    const resolvedOrg = org ?? global.login.selectedOrganization();
    if (!resolvedOrg) {
        return false;
    }
    return getEadmRole(resolvedOrg) <= Permission.DepartmentAdmin
        ? resolvedOrg.managerscanmanagepasswords
        : true
}

export function getNestedObjectProperty(obj: any, path: string): any {
    if (!obj) {
        return null;
    }
    // Traverses the path for an object and return the value.
    // For example we could have an ICompanySyncStep and the path "syncstep.name" which would
    // parse the ICompanySyncStep, retrieve its syncstep object and then return its name.
    return path.split('.').reduce((o: any, key: string) => o[key], obj);
}

export function isPrimitive(val: any): boolean {
    switch (typeof val) {
        case "boolean":
        case "number":
        case "string":
        case "bigint":
        case "symbol":
            return true;
    }
    return false;
}

export function getSourceTranslation(_sf: nsSyncFields.TSyncfield_basic, translate: boolean = global.sourceTranslation()): string {
    let ret: string = ''
    const prefixTranslation = storeObservable('prefix.translation')();
    if (!_sf?.name) {
        _d.warn('No syncfield:', _sf);
        if (prefixTranslation) {
            ret = nsSyncFields.getTranslationIcon(_sf)
        }
        return ret
    }
    const sf = lm_.getInstance(_sf.objecttypeid)?.getByKey(_sf.name)
    if (sf) {
        ret += translate
            ? sf?.displayname || sf?.name
            : sf?.name
    } else {
        ret += _sf.name || 'N/A'
    }
    if (prefixTranslation && sf) {
        ret = nsSyncFields.getTranslationIcon(sf) + ret
    }
    return ret
}

export function getSourceTranslationByKey(key: TSyncfield, arr: ISyncField[], translate: boolean = global.sourceTranslation()): string {
    const sf = getSyncField(key, arr);
    if (!sf) {
        return key;
    }
    return getSourceTranslation(sf, translate)
}

let counter = 0;
export const getNextId = (): number => {
    return ++counter;
};

export const copyToClipboard = (value: any, subject?: string): void => {
    try {
        const el = document.createElement('textarea');
        el.value = String(value);
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        const selection = document.getSelection();
        const selected =
            selection && selection.rangeCount > 0
                ? selection.getRangeAt(0)
                : false;
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        if (selected && selection) {
            selection.removeAllRanges();
            selection.addRange(selected);
        }
        global.snackbarData({message: `copied subject to clipboard|s:<code>${subject ?? value}</code>`});
    } catch (err) {
        global.snackbarData(createErrorMessage(String(err)));
    }
}

export function isBit(bit: number, allBits: number): boolean {
    return !!isBitNum(bit, allBits);
}

function isBitNum(bit: number, allBits: number): number {
    return allBits & bit;
}

export function retrieveGetParameters(): { [key: string]: string } {
    return nsDom.retrieveGetParameters();
}

export function isPartOf<T>(needle: T | (() => T), ...haystack: T[]): boolean {
    return haystack.includes(getAnyValue(needle));
}

export function isEnabledComputedFactory(params: {
    enable?: TBaseOrObservableOrFunctionType<boolean>,
    disable?: TBaseOrObservableOrFunctionType<boolean>
}) {
    if ('enable' in params) {
        const prop = params.enable;
        return ko.computed({
            read: (): boolean => {
                return !!getAnyValue(prop);
            },
            write: (bool: boolean) => {
                if (ko.isObservable(prop)) {
                    prop(bool);
                }
            }
        })
    } else if ('disable' in params) {
        const prop = params.disable;
        return ko.computed({
            read: (): boolean => {
                return !getAnyValue(prop);
            },
            write: (bool: boolean) => {
                if (ko.isObservable(prop)) {
                    prop(!bool);
                }
            }
        })
    }
    return ko.pureComputed(() => true);
}

/*
Importformats:
    id: 1, name: 'PAGA'
    id: 2, name: 'PAGA Hierachy'
    id: 3, name: 'Visma'
    id: 4, name: 'Visma Hierachy'
    id: 5, name: 'Agresso'
    id: 6, name: 'SD Worx'
    id: 7, name: 'Identum csv'
    id: 8, name: 'CatalystOne'
 */
export type TImportFormatKey =
    'PAGA'
    | 'PAGA Hierachy'
    | 'Visma'
    | 'Visma Hierachy'
    | 'Agresso'
    | 'SD Worx'
    | 'Identum csv'
    | 'CatalystOne'
    | number;

export function isImportFormat(key: TImportFormatKey, importFormat: IdName | null | undefined = global.login.selectedOrganization()?.importformat): boolean {
    if (!importFormat) {
        return false;
    }
    if (Number.isInteger(key)) {
        return importFormat.id === key;
    }
    return importFormat.name == key;
}

export function convertServerBoolean(boolStr: string): boolean | null {
    switch (String(boolStr).toLowerCase()) {
        case "true":
            return true
        case "false":
            return false
        default:
            return null
    }
}

// returns the search key with escaped regex symbols
export function getWhiteWashedSearchKey(searchkey: string): string {
    return searchkey.replace(/[-\/\\^$*+?.()|[\]{}]/gi, '\\$&');
}

export function escapeHTML(str: string): string {
    const entities: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;',
    }
    return str.replace(/[&<>'"]/g, (tag) => entities[tag] ?? tag);
}

export function getSearchMarkup(haystack: any, needle: string): string {
    const whiteWashedNeedle = getWhiteWashedSearchKey(needle)
    const exp = `(?<!<[^>]*)(${whiteWashedNeedle})`
    const regExp = new RegExp(exp, 'gim')
    return String(haystack).replace(regExp, (match: string) => {
        return "<mark>" + match + "</mark>"
    });
}

export interface IKnockoutThrottle {
    rateLimit?: number
}

export function getKnockoutThrottle(options?: IKnockoutThrottle) {
    return {
        rateLimit: {
            timeout: options?.rateLimit ?? REQUEST_THROTTLE,
            method: (action: Function, timeout: number) => {
                let timeoutId: number;
                return () => {
                    clearTimeout(timeoutId);
                    timeoutId = setTimeout(() => action(), timeout)
                }
            }
        }
    }
}

export function convertToArray<T>(obj: T | T[]): T[] {
    return Array.isArray(obj)
        ? obj
        : [obj]
}

export function isSupportedIcon(bool: TBaseOrObservableType<boolean>): string {
    const value = ko.utils.unwrapObservable(bool);
    const icon = value ? 'fa-check' : 'fa-ban'
    return i(icon) + ' ' + isSupported(value)
}

export function isSupported(bool: TBaseOrObservableOrFunctionType<boolean>): string {
    const b = getAnyValue(bool);
    return lm.t(b ? 'yes' : 'no') ?? ''
}

export function isSupportedCircle(bool: TBaseOrObservableType<boolean>, subject?: string): string {
    const value = ko.utils.unwrapObservable(bool);
    const icon = value ? 'fa-check-circle' : 'fa-times-circle'
    const text = value ? 'text-success' : 'text-danger'
    const yesNo = value ? 'yes' : 'no'
    const title = subject ? `((${subject})): ((${yesNo}))` : yesNo
    return `<i class="${icon} ${text} far fa-fw" aria-hidden="true" title="${lm.t(title)}"></i>`
}

export function stringSplitCondition(str: string, condition: (char: string, index: number) => boolean): string[] {
    const ret: string[] = []
    let lastIndex = 0;
    _.forEach(str, (c: string, index: number) => {
        if (condition(c, index)) {
            ret.push(str.slice(lastIndex, index))
            lastIndex = index + 1;
        }
        const lastCharProcessed = index == str.length - 1;
        if (lastCharProcessed) {
            ret.push(str.slice(lastIndex, str.length))
        }
    })
    return ret
}

export function getPercent(currentNum: number, totalNum: number, options: Intl.NumberFormatOptions = {}): string {
    return toLocaleString((currentNum / totalNum) * 100, options) + '%'
}

export function getRoleName(system: TSystemPartial, role: TSystemRolePartial) {
    if (system.name == SYSTEM_MEGA_EADM) {
        if (role.roleenum) {
            return lm.t(permissionDefinitions[role.roleenum as Permission]?.displayName ?? role.name)
        } else {
            return role.name
        }
    } else {
        return role.name
    }
}

export function decodeHtml(html: string) {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
}

export function stripHtml(html: string) {
    const div = document.createElement('div');
    div.innerHTML = html
    return div.innerText
}

export function decodeAndStripHtml(html: string) {
    return stripHtml(decodeHtml(html))
}

export function isKnockoutCompoonent(obj: any): boolean {
    const o = getAnyValue(obj);
    if ('name' in o) {
        return ComponentRegistry.includes(o.name)
    }
    return false
}

export function hasMultipleImportFormatTypes(): boolean {
    const org = global.login.selectedOrganization();
    return org ? nsDataSource.hasMultipleImportFormatTypes(org.importformats) : false;
}

export function getDataSourceTypesFromDataSourceIds(ids: number | number[]): TDataSourceType[] {
    const importformats = global.login.selectedOrganization()?.importformats ?? [];
    const idList = Array.isArray(ids) ? ids : [ids];
    return _(idList)
        .map((id: number) => importformats.find((fmt: IImportFormat) => fmt.id == id))
        .filter((o: IImportFormat | undefined): o is IImportFormat => !!o)
        .map((o: IImportFormat) => nsDataSource.getImportFormatType(o))
        .value()
}

export function hasDataSourceType(type: TDataSourceType): boolean {
    return global.login.selectedOrganization()?.importformats.some(o => nsDataSource.getImportFormatType(o) == type) ?? false
}

export function getDataSourceTypesFromObject(object: IObjectData | null | undefined): TDataSourceType[] {
    let ids: number | number[] | undefined;
    if (object) {
        const aauthDataSource = getRawSyncFieldValue('AauthDataSource', object.syncfields);
        const authDataSource = getRawSyncFieldValue('AuthDataSource', object.syncfields);
        if (aauthDataSource) {
            ids = nsSyncFields.getArrayByString(aauthDataSource).map(Number)
        } else if (authDataSource) {
            ids = Number(authDataSource);
        }
    }
    if (ids === undefined) {
        ids = global.login.selectedOrganization()?.importformat.id
    }
    if (ids === undefined) {
        return []
    }
    return getDataSourceTypesFromDataSourceIds(ids)
}

/** Stored ClientCredential ExtensionAttribute5 vs profile integrationcode (getclientcredentialprofile). */
export function credentialMatchesProfile(
    credentialIntegrationCode: string | null | undefined,
    profileIntegrationCode: string,
): boolean {
    if (!credentialIntegrationCode) {
        return false;
    }
    const stored = credentialIntegrationCode.trim();
    const profile = profileIntegrationCode.trim();
    if (!profile) {
        return false;
    }
    if (stored === profile) {
        return true;
    }
    const colonIdx = profile.indexOf(':');
    if (colonIdx > 0) {
        const profileBase = profile.substring(0, colonIdx);
        if (stored === profileBase) {
            return true;
        }
    }
    return false;
}